import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCreateMarket, useMarkets, useUpdateMarket } from '@/api/domains/markets';
import type { Market } from '@/api/types/services';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormMultiCountryField } from '@/components/form';
import { resolveLocale } from '@/i18n';
import { applyApiErrors, toApiProblem } from '@/lib/errors';

import { suggestMarketName } from './market-names';
import {
  CARRIER_COUNTRIES,
  defaultMarketForm,
  marketFormSchema,
  type MarketFormValues,
} from './schemas';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: Market | null;
};

export function AddMarketDialog({ open, onOpenChange, editing }: Props) {
  const { t, i18n } = useTranslation('markets');
  const tCommon = useTranslation('common').t;
  const locale = resolveLocale(i18n.language);
  const create = useCreateMarket();
  const update = useUpdateMarket(editing?.id ?? 0);
  const marketsQuery = useMarkets();

  // Markets are disjoint: a country already grouped in another market can't be
  // picked again (the backend enforces this too - DisjointMarketCountries).
  const disabledCountries = useMemo(() => {
    const taken: Record<string, string> = {};
    for (const market of marketsQuery.data ?? []) {
      if (editing && market.id === editing.id) continue;
      for (const code of market.countries) {
        taken[code.toUpperCase()] = market.name;
      }
    }
    return taken;
  }, [marketsQuery.data, editing]);

  const form = useForm<MarketFormValues>({
    resolver: zodResolver(marketFormSchema),
    defaultValues: defaultMarketForm,
  });

  // Re-seed the form every time the dialog opens (create starts blank, edit pre-fills).
  useEffect(() => {
    if (!open) return;
    form.reset(
      editing
        ? {
            // Narrow instead of cast - drop any country the picker doesn't support.
            countries: editing.countries.filter(
              (code): code is MarketFormValues['countries'][number] =>
                (CARRIER_COUNTRIES as readonly string[]).includes(code),
            ),
            name: editing.name,
          }
        : defaultMarketForm,
    );
  }, [open, editing, form]);

  // Suggest a name from the selected countries until the merchant edits the field.
  const countries = form.watch('countries');
  useEffect(() => {
    if (!open || editing || form.getFieldState('name').isDirty) return;
    form.setValue('name', suggestMarketName(countries, locale));
  }, [open, editing, countries, locale, form]);

  const onSubmit = (values: MarketFormValues) => {
    const mutation = editing ? update : create;
    mutation.mutate(values, {
      onSuccess: () => {
        toast.success(editing ? t('marketUpdated') : t('marketCreated'));
        onOpenChange(false);
      },
      onError: (error) => applyApiErrors(form, toApiProblem(error)),
    });
  };

  const isPending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[14px] sm:max-w-[660px] sm:rounded-[14px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium leading-none">
            {editing ? t('dialog.editTitle') : t('dialog.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('dialog.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 py-2">
            <FormMultiCountryField<MarketFormValues>
              name="countries"
              label={t('dialog.fields.countries')}
              countries={CARRIER_COUNTRIES}
              disabledCountries={disabledCountries}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-sm font-medium leading-none text-black">
                    {t('dialog.fields.name')}
                  </FormLabel>
                  {/* Figma places the helper between the label and the input. */}
                  <p className="text-sm text-muted-foreground">{t('dialog.fields.nameHint')}</p>
                  <FormControl>
                    <Input
                      placeholder={t('dialog.fields.namePlaceholder')}
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('dialog.close')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? tCommon('actions.saving') : t('dialog.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
